import { useEffect, useState } from "react";
import { Menu, X, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { label: "Problem", id: "#problem", test: "nav-link-problem" },
  { label: "How It Works", id: "#how-it-works", test: "nav-link-how-it-works" },
  { label: "Pricing", id: "#pricing", test: "nav-link-pricing" },
  { label: "FAQ", id: "#faq", test: "nav-link-faq" },
  { label: "Contact", id: "#contact", test: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(id);
  };

  const launch = () => {
    setOpen(false);
    scrollToId("#pricing");
    window.dispatchEvent(new CustomEvent("grapho:highlight-trial"));
  };

  return (
    <header data-testid="sticky-navbar" className="fixed inset-x-0 top-4 z-50 px-4">
      <nav
        className={`mx-auto flex max-w-6xl items-center justify-between gap-4 rounded-full border px-4 py-3 transition-all duration-500 sm:px-5 ${
          scrolled
            ? "border-white/10 bg-[#090F1E]/90 shadow-[0_12px_40px_rgba(0,0,0,0.6)] backdrop-blur-xl"
            : "border-white/5 bg-[#090F1E]/50 backdrop-blur-md"
        }`}
      >
        <a href="#top" onClick={go("#top")} data-testid="brand-logo" className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60 animate-ping-slow" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
          </span>
          <span className="text-lg font-extrabold tracking-tight text-white">GRAPHO</span>
          <span className="hidden rounded-full border border-white/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 md:inline">
            Human POD Atelier
          </span>
        </a>

        <div className="hidden items-center gap-7 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.id}
              data-testid={l.test}
              onClick={go(l.id)}
              className="text-sm text-slate-300 transition-colors duration-300 hover:text-white"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            href="/login"
            data-testid="nav-login-button"
            className="hidden items-center rounded-full border border-amber-400/40 px-5 py-2.5 text-sm font-bold text-amber-300 transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-300 hover:text-amber-200 sm:inline-flex"
          >
            Login
          </a>
          <button
            data-testid="nav-cta-launch-trial"
            onClick={launch}
            className="hidden items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-[#1A1204] transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-300 hover:shadow-[0_0_28px_rgba(245,158,11,0.45)] sm:inline-flex"
          >
            <Sparkles className="h-4 w-4" />
            $1 Launch Trial
          </button>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-200 transition-colors hover:border-white/25 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="mx-auto mt-2 max-w-6xl rounded-2xl border border-white/10 bg-[#090F1E]/95 p-4 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col">
            {LINKS.map((l) => (
              <a
                key={l.id}
                href={l.id}
                data-testid={`${l.test}-mobile`}
                onClick={go(l.id)}
                className="rounded-xl px-4 py-3 text-sm text-slate-200 transition-colors hover:bg-white/5"
              >
                {l.label}
              </a>
            ))}
            <a
              href="/login"
              data-testid="nav-login-button-mobile"
              className="mt-2 inline-flex items-center justify-center rounded-full border border-amber-400/40 px-5 py-3 text-sm font-bold text-amber-300"
            >
              Login
            </a>
            <button
              data-testid="nav-mobile-cta"
              onClick={launch}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-5 py-3 text-sm font-bold text-[#1A1204]"
            >
              <Sparkles className="h-4 w-4" />
              $1 Launch Trial
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
